import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import { ArrowRight, Check, Loader2, Package, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import truweLogo from "@/assets/opt/truwe-logo-precision.webp";
import { useCartStore } from "@/stores/cartStore";
import { isComingSoon, PREPAID_ONLY, SOLD_OUT } from "@/lib/storeStatus";
import {
  RETURNING_OFFER, RETURNING_PRODUCT_ART, hideReturningOffer, offerImageSources,
  productHandleFromPath, readReturningVisitor, returningOfferCandidates,
  type OfferProduct, type ReturningVisitor,
} from "./returning-offer";
import "./returning-reminder.css";

function sessionFlag(key: string): boolean {
  try { return sessionStorage.getItem(key) === "1"; } catch { return false; }
}

function markSession(key: string): void {
  try { sessionStorage.setItem(key, "1"); } catch { /* in-memory refs still suppress this mount */ }
}

function hasOtherDialog(): boolean {
  return Array.from(document.querySelectorAll<HTMLElement>(
    '[role="dialog"], [role="alertdialog"], [aria-modal="true"], dialog[open]',
  )).some((node) => !node.hasAttribute("data-truwe-returning-offer")
    && !node.closest("[data-truwe-returning-offer]")
    && node.getAttribute("data-state") !== "closed"
    // Always-mounted closed panels (e.g. the mobile menu) are aria-hidden/inert.
    && !node.closest('[aria-hidden="true"], [inert]')
    && node.getClientRects().length > 0
    && getComputedStyle(node).visibility !== "hidden"
    && getComputedStyle(node).opacity !== "0");
}

function OfferArtwork({ product }: { product: OfferProduct }) {
  const art = RETURNING_PRODUCT_ART[product.handle];
  const [failed, setFailed] = useState<string[]>([]);
  const src = offerImageSources(product).find((url) => !failed.includes(url));
  return (
    <aside className="tw-returning__art" aria-label={product.title}>
      <img className="tw-returning__logo" src={truweLogo} alt="TruWe" />
      <div className="tw-returning__image" data-poster={!!src && src === art?.src}>
        {src ? <img key={src} src={src} alt={`${product.title} packaging`} decoding="async"
          onError={() => setFailed((values) => [...values, src])} />
          : <Package aria-hidden="true" className="tw-returning__fallback" />}
      </div>
      <div className="tw-returning__product">
        <strong>{product.title}</strong>
        {art?.caption && <span>{art.caption}</span>}
      </div>
    </aside>
  );
}

/** One reusable popup for all active, published catalogue products. */
export function ReturningReminder() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();
  const items = useCartStore((state) => state.items);
  const appliedCoupon = useCartStore((state) => state.appliedCoupon);
  const [visitor, setVisitor] = useState<ReturningVisitor | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [otherDialog, setOtherDialog] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const shown = useRef(false);
  const requestId = useRef(0);
  const currentPath = useRef(pathname);
  currentPath.current = pathname;
  const inFlight = useRef(false);
  const previousFocus = useRef<HTMLElement | null>(null);
  const continuation = useRef<(() => void) | null>(null);
  const hidden = hideReturningOffer(pathname, typeof window === "undefined" ? "" : window.location.hostname);
  const candidates = returningOfferCandidates(pathname, visitor?.primaryInterest ?? null,
    items.map((item) => item.productHandle));

  useEffect(() => {
    setDismissed(sessionFlag(RETURNING_OFFER.dismissKey));
    shown.current = sessionFlag(RETURNING_OFFER.shownKey);
    const accept = (input: unknown) => {
      const detail = readReturningVisitor(input);
      if (detail && !sessionFlag(RETURNING_OFFER.dismissKey)) setVisitor(detail);
    };
    try {
      const saved = sessionStorage.getItem(RETURNING_OFFER.visitorKey);
      if (saved) accept(JSON.parse(saved));
    } catch { /* denied storage or stale/malformed value: wait for the real event */ }
    const onReturning = (event: Event) => accept((event as CustomEvent<unknown>).detail);
    window.addEventListener("tw:returning-visitor", onReturning);
    return () => {
      window.removeEventListener("tw:returning-visitor", onReturning);
      requestId.current += 1;
    };
  }, []);

  const { data: product } = useQuery({
    queryKey: ["returning-offer-product", candidates],
    enabled: !!visitor && !dismissed && !hidden && !appliedCoupon && candidates.length > 0,
    staleTime: 60_000,
    retry: false,
    queryFn: async () => {
      const { fetchProductByHandle } = await import("@/lib/shopify");
      for (const handle of candidates) {
        // This existing accessor excludes inactive, unpublished and internal test products.
        const candidate = await fetchProductByHandle(handle);
        if (candidate?.title?.trim()) return candidate;
      }
      return null;
    },
  });

  const dismiss = useCallback(() => {
    requestId.current += 1;
    inFlight.current = false;
    setBusy(false);
    setOpen(false);
    setDismissed(true);
    markSession(RETURNING_OFFER.dismissKey);
  }, []);

  useEffect(() => {
    if (!visitor || dismissed || hidden || appliedCoupon) return;
    const inspect = () => setOtherDialog(hasOtherDialog());
    inspect();
    const observer = new MutationObserver(inspect);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true,
      attributeFilter: ["role", "aria-modal", "data-state", "hidden", "open", "style", "class"] });
    return () => observer.disconnect();
  }, [visitor, dismissed, hidden, appliedCoupon]);

  useEffect(() => {
    if (!visitor || !product || dismissed || hidden || appliedCoupon || otherDialog || shown.current) return;
    const timer = window.setTimeout(() => {
      if (hasOtherDialog() || sessionFlag(RETURNING_OFFER.dismissKey) || useCartStore.getState().appliedCoupon) return;
      shown.current = true;
      markSession(RETURNING_OFFER.shownKey);
      previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setOpen(true);
    }, RETURNING_OFFER.delayMs);
    return () => window.clearTimeout(timer);
  }, [visitor, product, pathname, dismissed, hidden, appliedCoupon, otherDialog]);

  // A route transition or a newer dialog invalidates any pending apply response.
  const lastPath = useRef(pathname);
  useEffect(() => {
    const changed = lastPath.current !== pathname;
    lastPath.current = pathname;
    if (open && (changed || hidden || otherDialog || appliedCoupon)) dismiss();
  }, [pathname, hidden, otherDialog, appliedCoupon, open, dismiss]);

  const apply = async () => {
    if (!product || inFlight.current || !open) return;
    if (useCartStore.getState().appliedCoupon) { dismiss(); return; }
    inFlight.current = true;
    setBusy(true);
    const thisRequest = ++requestId.current;
    try {
      const { validateCoupon } = await import("@/lib/coupons.functions");
      const result = await validateCoupon({ data: { code: RETURNING_OFFER.code } });
      if (thisRequest !== requestId.current || currentPath.current !== pathname || hasOtherDialog()) return;
      if (!result.ok) {
        toast.error(result.reason === "already_used" ? "This offer is for first orders only."
          : "This offer isn't available right now. Check offers in your cart.");
        return;
      }
      if (result.code !== RETURNING_OFFER.code || result.discountType !== "percent"
        || result.discountPercent !== RETURNING_OFFER.percent || result.prepaidOnly
        || (result.minSubtotalPaise ?? 0) > 0 || result.maxDiscountPaise != null
        || !(Date.parse(result.expiresAt) > Date.now())) {
        toast.error("This offer has changed. Check available offers in your cart.");
        return;
      }
      // An auto-applied or manually selected coupon always wins an in-flight race.
      if (useCartStore.getState().appliedCoupon) { dismiss(); return; }
      const handle = product.handle;
      const hasProductInCart = useCartStore.getState().items.some((item) => item.productHandle === handle);
      continuation.current = () => {
        const openCart = (window as unknown as { __openCart?: () => void }).__openCart;
        if (hasProductInCart && !isComingSoon(handle) && !SOLD_OUT && openCart) { openCart(); return; }
        if (productHandleFromPath(pathname) === handle) {
          document.getElementById("buy-box")?.scrollIntoView({
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
            block: "center",
          });
        } else {
          void navigate({ to: "/products/$handle", params: { handle } });
        }
      };
      useCartStore.getState().setCoupon({
        code: result.code,
        discountPercent: result.discountPercent,
        prepaidOnly: result.prepaidOnly,
        expiresAt: result.expiresAt,
        discountType: result.discountType,
        maxDiscountAmount: null,
      });
      toast.success("WELCOME10 applied. First-order eligibility is checked at checkout.");
      dismiss();
    } catch {
      if (thisRequest === requestId.current) toast.error("Couldn't apply the offer. Please try again.");
    } finally {
      if (thisRequest === requestId.current) { inFlight.current = false; setBusy(false); }
    }
  };

  const comingSoon = !!product && isComingSoon(product.handle);
  return (
    <Dialog.Root open={open && !!product && !hidden && !otherDialog && !appliedCoupon}
      onOpenChange={(next) => { if (!next) dismiss(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="tw-returning__overlay" />
        <Dialog.Content className="tw-returning" data-truwe-returning-offer=""
          data-tone={product ? RETURNING_PRODUCT_ART[product.handle]?.tone ?? "neutral" : "neutral"}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const next = continuation.current;
            continuation.current = null;
            if (next) next();
            else if (previousFocus.current?.isConnected) previousFocus.current.focus({ preventScroll: true });
          }}>
          <Dialog.Close className="tw-returning__close" aria-label="Close offer"><X aria-hidden="true" /></Dialog.Close>
          {product && <OfferArtwork key={product.handle} product={product} />}
          <div className="tw-returning__body">
            <p className="tw-returning__welcome"><RotateCcw aria-hidden="true" />Good to see you again</p>
            <Dialog.Title className="tw-returning__title">Still thinking about <strong>{product?.title}?</strong></Dialog.Title>
            <Dialog.Description className="tw-returning__offer">
              <span className="tw-returning__discount">{RETURNING_OFFER.percent}<span>%</span><small>off</small></span>
              <span className="tw-returning__offer-label">On your first<br />TruWe order.</span>
            </Dialog.Description>
            <div className="tw-returning__coupon"><span>Your welcome code</span><code>{RETURNING_OFFER.code}</code></div>
            <button type="button" className="tw-returning__apply" onClick={() => void apply()} disabled={busy} aria-busy={busy}>
              {busy ? "Applying…" : "Apply 10% & continue"}
              {busy ? <Loader2 className="tw-returning__spin" aria-hidden="true" /> : <ArrowRight aria-hidden="true" />}
            </button>
            {comingSoon ? <p className="tw-returning__availability">{product?.title} is on pre-booking. No payment today.</p>
              : SOLD_OUT ? <p className="tw-returning__availability">Check current availability on the product page.</p>
              : <p className="tw-returning__payment"><Check aria-hidden="true" />{PREPAID_ONLY ? "Available on prepaid orders" : "Works with prepaid & COD"}</p>}
            <p className="tw-returning__terms">First orders only. Eligibility checked at checkout.</p>
            <Dialog.Close className="tw-returning__secondary">Keep exploring</Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default ReturningReminder;
